// Hideo - Italian translations: editor (scripts, editor, comments, textstyles, teleprompter) and the Scripts page.
// Keys are the English source strings used in the code (see docs/i18n.md).
import type { Dictionary } from '../types'

const it: Dictionary = {
    // Shared short labels (same Italian as in the other dictionaries)
    'Close': 'Chiudi',
    'Edit': 'Modifica',
    'Rename': 'Rinomina',
    'Insert': 'Inserisci',
    'Text': 'Testo',
    'Comments': 'Commenti',
    'Hook': 'Hook',

    // Editor shell (ScriptEditor.vue, src/components/editor/): top bar, toolbar, footer
    'Script title': 'Titolo script',
    'Visual': 'Visuale',
    'Save as template': 'Salva come template',
    'Delete script': 'Elimina script',
    'Download': 'Scarica',
    'Table of contents': 'Indice dei titoli',
    'Undo': 'Annulla',
    'Redo': 'Ripeti',
    'Style': 'Stile',
    'Decrease': 'Riduci',
    'Increase': 'Aumenta',
    'Text colour (the line shows the colour of the selected text)': 'Colore del testo (la riga mostra il colore del testo selezionato)',
    'Pick another text colour': 'Scegli un altro colore del testo',
    'Highlight (the line shows the highlight of the selected text)': 'Evidenzia (la riga mostra l\'evidenziatura del testo selezionato)',
    'Pick another highlighter colour': 'Scegli un altro colore dell\'evidenziatore',
    'Alignment': 'Allineamento',
    'Line spacing': 'Interlinea',
    'Lists and indents': 'Elenchi e rientri',
    'Add a comment (Ctrl+Alt+M)': 'Aggiungi un commento (Ctrl+Alt+M)',
    'More tools': 'Altri strumenti',
    '1.0': '1,0',
    '1.25': '1,25',
    '1.5': '1,5',
    '1.75': '1,75',
    '2.0': '2,0',
    '0 words': '0 parole',

    // scripts.js: built-in templates (names and inserted headings)
    'Hook · Body · CTA': 'Hook · Corpo · CTA',
    'Hook (0-5 sec)': 'Hook (0-5 sec)',
    'Context': 'Contesto',
    'Development': 'Sviluppo',
    'Closing and CTA': 'Chiusura e CTA',
    'Short Form (60 sec)': 'Short Form (60 sec)',
    'Point 1': 'Punto 1',
    'Point 2': 'Punto 2',
    'Surprise ending': 'Finale a sorpresa',
    'CTA': 'CTA',
    'Long Form (story)': 'Long Form (storia)',
    'Screenplay (Fountain)': 'Sceneggiatura (Fountain)',
    // The built-in screenplay (src/features/video.ts): the scene heading words and the transition stay in English
    'My short film': 'Il mio cortometraggio',
    'Written by': 'Scritto da',
    'Your name': 'Il tuo nome',
    'KITCHEN': 'CUCINA',
    'Anna stirs a pot. The radio plays softly.': 'Anna mescola una pentola. La radio suona piano.',
    'without turning around': 'senza voltarsi',
    'You are late again.': 'Sei di nuovo in ritardo.',
    'The bus never came.': 'L\'autobus non è mai arrivato.',
    'Main part': 'Svolgimento',
    'Plot twist': 'Colpo di scena',
    'Conclusion': 'Conclusione',
    'References': 'Fonti',

    // scripts.js: video dialog, starting point, tabs (default names are stored: shown with tv())
    'Delete this script permanently?': 'Eliminare definitivamente questo script?',
    "This video's scripts": 'Script di questo video',
    'Untitled script': 'Script senza titolo',
    'Open editor': 'Apri editor',
    'New script for this video': 'Nuovo script per questo video',
    'Create script': 'Crea script',
    'Where do you want to start?': 'Da dove parti?',
    'Blank': 'Vuoto',
    'Blank page': 'Foglio bianco',
    'Template': 'Template',
    'Your template': 'Tuo template',
    'Start from a hook': 'Parti da un hook',
    'Load a .md / .txt / .fountain file': 'Carica un file .md / .txt / .fountain',
    'Scripts without a video: attach them to this video': 'Script senza video: ricollegali a questo video',
    'Attach here': 'Collega qui',
    'New script': 'Nuovo script',
    'Template name:': 'Nome del template:',
    'Template saved': 'Template salvato',
    'Click to rename': 'Clic per rinominare',
    'Open · double-click to rename': 'Apri · doppio clic per rinominare',
    'Delete tab': 'Elimina la scheda',
    'New tab': 'Nuova scheda',
    'Tab name:': 'Nome della scheda:',
    'Draft {n}': 'Bozza {n}',
    'Delete this tab?': 'Eliminare questa scheda?',

    // writing surface (src/features/editor/surface/): status, word count, links
    'Saving the script…': 'Salvataggio…',
    '{n} word · ~{m} min read': '{n} parola · ~{m} min di lettura',
    '{n} words · ~{m} min read': '{n} parole · ~{m} min di lettura',
    '{n} word · ~{m} min {s} s spoken': '{n} parola · ~{m} min {s} s di parlato',
    '{n} words · ~{m} min {s} s spoken': '{n} parole · ~{m} min {s} s di parlato',
    'Link address (URL):': 'Indirizzo del link (URL):',
    'Content': 'Contenuto',
    'Open in a new tab': 'Apri in una nuova scheda',
    'Invalid link': 'Link non valido',
    'Edit link': 'Modifica il link',
    'Copy address': "Copia l'indirizzo",
    'Remove link': 'Togli il link',
    'Invalid address: it must start with http://, https:// or www.': 'Indirizzo non valido: deve iniziare con http://, https:// o www.',
    'Link copied': 'Link copiato',

    // writing surface: "/" menu, block menu, toolbar drop-downs
    'Heading 1': 'Titolo 1',
    'Heading 2': 'Titolo 2',
    'Heading 3': 'Titolo 3',
    'Heading 4': 'Titolo 4',
    'Bulleted list': 'Elenco puntato',
    'Numbered list': 'Elenco numerato',
    'Checklist': 'Elenco di controllo',
    'Toggle list': 'Elenco a comparsa',
    'Code': 'Codice',
    'Quote': 'Citazione',
    'Callout': 'Callout',
    'Divider': 'Divisore',
    'Link to an external site': 'Link a sito esterno',
    'Turn into': 'Trasforma in',
    'Move up': 'Sposta su',
    'Move down': 'Sposta giù',
    'Duplicate': 'Duplica',
    'Align left': 'Allinea a sinistra',
    'Centre': 'Centra',
    'Align right': 'Allinea a destra',
    'Justify': 'Giustifica',
    'Decrease indent': 'Riduci rientro',
    'Increase indent': 'Aumenta rientro',
    'Contents': 'Indice',
    'The script headings appear here (Heading 1, 2 and 3): tap one to jump to it.': 'Qui compaiono i titoli dello script (Titolo 1, 2 e 3): toccane uno per raggiungerlo.',

    // writing surface: selection menu
    'Normal text': 'Testo normale',
    'Bold': 'Grassetto',
    'Italic': 'Corsivo',
    'Underline': 'Sottolineato',
    'Strikethrough': 'Barrato',
    'Text colour': 'Colore del testo',
    'Highlight': 'Evidenzia',
    'Link': 'Link',
    'Clear formatting': 'Cancella formattazione',
    'All caps': 'Tutto maiuscolo',
    'All lowercase': 'Tutto minuscolo',
    'Tabs': 'Schede',
    'Comment (Ctrl+Alt+M)': 'Commenta (Ctrl+Alt+M)',
    'Other colour': 'Altro colore',
    'Normal colour': 'Colore normale',
    'No highlight': 'Nessuna evidenziazione',

    // comments.js ('You' is the stored default author: shown with tv())
    'You': 'Tu',
    'Comments are added in the Visual view.': 'I commenti si aggiungono nella vista Visuale.',
    'First select the piece of text to comment on.': 'Seleziona prima il pezzo di testo da commentare.',
    "This part can't be commented on: try selecting normal text.": 'Questo pezzo non si può commentare: prova a selezionare del testo normale.',
    'Delete this comment and its replies?': 'Eliminare questo commento e le sue risposte?',
    'edited': 'modificato',
    'Resolve: remove the highlight and archive': "Risolvi: togli l'evidenziatura e archivia",
    'The commented text was deleted': 'Il testo commentato è stato eliminato',
    'Reply…': 'Rispondi…',
    'Reply': 'Rispondi',
    'Write a comment…': 'Scrivi un commento…',
    'Comment': 'Commenta',
    'Ctrl+Enter to save · Esc to cancel': 'Ctrl+Invio per salvare · Esc per annullare',
    'Select a piece of text and press {icon} <b>Comment</b> to leave yourself a note.': 'Seleziona un pezzo di testo e premi {icon} <b>Commenta</b> per lasciarti una nota.',
    'Resolved ({n})': 'Risolti ({n})',
    'Resolved {date}': 'Risolto {date}',
    '{n} reply': '{n} risposta',
    '{n} replies': '{n} risposte',

    // textstyles.js: fonts and styles
    '{font} Thin': '{font} Sottile',
    '{font} Extra light': '{font} Extra leggero',
    '{font} Light': '{font} Leggero',
    '{font} Regular': '{font} Normale',
    '{font} Medium': '{font} Medio',
    '{font} Semi bold': '{font} Semi grassetto',
    '{font} Bold': '{font} Grassetto',
    '{font} Extra bold': '{font} Extra grassetto',
    '{font} Black': '{font} Nero',
    'Sans serif': 'Senza grazie',
    'Serif': 'Con grazie',
    'Display fonts': 'Titoli',
    'Handwriting': 'Scritto a mano',
    'Monospace': 'Monospazio',
    'System fonts': 'Di sistema',
    'Hide variants': 'Chiudi le varianti',
    'Show variants': 'Vedi le varianti',
    'Font': 'Font',
    'Style changed in this script': 'Stile modificato in questo script',
    'Edit the {name} style': 'Modifica lo stile {name}',
    'Update “{name}” to match the selected text': 'Aggiorna «{name}» come il testo selezionato',
    'Reset “{name}”': 'Ripristina «{name}»',
    'Save as my default styles': 'Salva come miei stili predefiniti',
    'Use my default styles': 'Usa i miei stili predefiniti',
    'Back to the original styles': 'Torna agli stili originali',
    'Delete my default styles': 'Cancella i miei stili predefiniti',
    'Put the cursor in (or select) the text to use as a model.': 'Metti il cursore (o seleziona) nel testo da usare come modello.',
    'Default styles saved: they apply to scripts without their own styles': 'Stili predefiniti salvati: valgono per gli script senza stili propri',

    // Scripts page and "Attach" dialog (ScriptsPage.vue, ScriptLinkDialog.vue)
    'Search scripts': 'Cerca negli script',
    'Your templates': 'I tuoi template',
    "You save them from a script's editor with the bookmark icon; you pick them when you create a new script.": "Li salvi dall'editor di uno script con l'icona del segnalibro; li scegli quando crei un nuovo script.",
    'All ({n})': 'Tutti ({n})',
    'No video ({n})': 'Senza video ({n})',
    '{n} word · ~{m} min {s} s': '{n} parola · ~{m} min {s} s',
    '{n} words · ~{m} min {s} s': '{n} parole · ~{m} min {s} s',
    '{n} tab': '{n} scheda',
    '{n} tabs': '{n} schede',
    'edited on {date}': 'modificato il {date}',
    'Open the video': 'Apri il video',
    'No video': 'Senza video',
    'Attach the script to a video': 'Collega lo script a un video',
    'Attach': 'Collega',
    'No scripts found.': 'Nessuno script trovato.',
    "No scripts yet: create one from a video's window, under “Script”.": 'Nessuno script ancora: crealo dalla finestra di un video, sotto "Script".',
    'No saved templates.': 'Nessun template salvato.',
    'In progress': 'In corso',
    'No scripts in progress.': 'Nessuno script in corso.',
    'Delete the script "{name}" permanently?': 'Eliminare definitivamente lo script "{name}"?',
    'Attach to a video': 'Collega a un video',
    'Search for a video': 'Cerca un video',
    '{n} script': '{n} script',
    '{n} scripts': '{n} script',
    'no scripts': 'nessuno script',
    'No videos in the open plan.': 'Nessun video nel PED aperto.',
    'Script attached to "{title}"': 'Script collegato a "{title}"',
    'New template name:': 'Nuovo nome del template:',
    'Delete the template "{name}"? Scripts created from this template are not affected.': 'Eliminare il template "{name}"? Gli script creati da questo template non vengono toccati.',
    // Teleprompter (TeleprompterDialog.vue)
    'Close (Esc)': 'Chiudi (Esc)',
    'Play/Pause (Space)': 'Avvia/Pausa (Spazio)',
    'Speed': 'Velocità',
    'Text size': 'Testo',
    'Mirror': 'Specchio',
    'From the top': 'Da capo',

    // Fountain mode (FountainEditor.vue, src/features/editor/fountain*.ts): the screenplay terms of docs/i18n.md
    'Screenplay in Fountain format': 'Sceneggiatura in formato Fountain',
    'Scene heading': 'Intestazione di scena',
    'Action': 'Azione',
    'Character': 'Personaggio',
    'Parenthetical': 'Parentetica',
    'Dialogue': 'Dialogo',
    'Transition': 'Transizione',
    'Centered': 'Centrato',
    'Lyrics': 'Testo cantato',
    'Section': 'Sezione',
    'Synopsis': 'Sinossi',
    'Note': 'Nota',
    'Page break': 'Interruzione di pagina',
    'Element': 'Elemento',
    'Tab: next element · Shift+Tab: previous · Enter: what comes next · Shift+Enter: new line': 'Tab: elemento successivo · Maiusc+Tab: precedente · Invio: quello che segue · Maiusc+Invio: a capo',
    'Write': 'Scrivi',
    'Preview': 'Anteprima',
    'Side by side': 'Affiancate',
    'Paper size': 'Formato della carta',
    'Print or save as PDF': 'Stampa o salva come PDF',
    'Scenes and characters': 'Scene e personaggi',
    'Scenes': 'Scene',
    'Scene headings (INT. / EXT.) and sections (#) appear here: tap one to jump to it.': 'Qui compaiono le intestazioni di scena (INT. / EXT.) e le sezioni (#): toccane una per andarci.',
    'Characters': 'Personaggi',
    'Go to their first line': 'Vai alla sua prima battuta',
    '{n} line': '{n} battuta',
    '{n} lines': '{n} battute',
    'The characters who speak appear here, with how many lines each one has.': 'Qui compaiono i personaggi che parlano, con quante battute ha ognuno.',
    'Write your screenplay here. Start with a scene heading, such as INT. KITCHEN - NIGHT': 'Scrivi qui la tua sceneggiatura. Inizia con un\'intestazione di scena, come INT. CUCINA - NIGHT',
    'The formatted screenplay appears here.': 'Qui compare la sceneggiatura impaginata.',
    '0 pages': '0 pagine',
    '{n} page · ~{m} min on screen': '{n} pagina · ~{m} min sullo schermo',
    '{n} pages · ~{m} min on screen': '{n} pagine · ~{m} min sullo schermo',

    // mini toolbar of the long text fields (MentionField.vue)
    'Formatting': 'Formattazione',
    'Headings': 'Titoli',
    'To-do list': 'Cose da fare',
    'Tag a video, hook, creator or source': 'Tagga un video, un hook, un creator o una fonte',
}

export default it
